/**
 * Phone number helpers for building `tel:` links.
 *
 * We intentionally avoid a full E.164 / libphonenumber validation here:
 * admins type numbers in many formats (with spaces, dashes, parentheses,
 * local or international), and `tel:` handlers accept most of them as long
 * as the value is reduced to an optional leading `+` plus digits.
 */

/**
 * Reduce a user-entered phone number to the form accepted by `tel:` URIs:
 * an optional leading `+` followed by digits only. All other characters
 * (spaces, dashes, parentheses, dots, letters) are stripped.
 */
export function sanitizePhoneForTel(value: string | null | undefined): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';
  return (hasPlus ? '+' : '') + digits;
}

/**
 * Whether a user-entered phone number looks callable.
 * Requires at least 5 digits after sanitization, which is enough to rule
 * out empty fields and obvious typos without rejecting short local numbers.
 */
export function isCallablePhone(value: string | null | undefined): boolean {
  const sanitized = sanitizePhoneForTel(value);
  const digitCount = sanitized.replace('+', '').length;
  return digitCount >= 5;
}

/**
 * Build a `tel:` href from a raw phone value, or return `null` if the
 * number isn't callable.
 */
export function telHref(value: string | null | undefined): string | null {
  if (!isCallablePhone(value)) return null;
  return `tel:${sanitizePhoneForTel(value)}`;
}

/**
 * Build an `sms:` href from a raw phone value, or return `null` if the
 * number isn't messageable. Mirrors `telHref` — the URI scheme accepts
 * the same sanitized format and we apply the same minimum-digit threshold.
 */
export function smsHref(value: string | null | undefined): string | null {
  if (!isCallablePhone(value)) return null;
  return `sms:${sanitizePhoneForTel(value)}`;
}

/**
 * Build a `https://wa.me/<digits>` href from a raw phone value, or return
 * `null` if the number isn't messageable. wa.me requires the international
 * format with no leading `+`; numbers stored without a country code may
 * still produce a link but WhatsApp will fail to resolve them.
 */
export function whatsappHref(value: string | null | undefined): string | null {
  if (!isCallablePhone(value)) return null;
  const digits = sanitizePhoneForTel(value).replace(/^\+/, '');
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}
